import type { TStateCreator } from "../types";

/**
 * Server-derived snapshot of a file's background whole-file operation
 * (auto-balance, generate-gl, run-adjustment, remove-generated-lines,
 * reprocess, revalidate) — the client-side mirror of
 * `GET /files/{fileId}/operation-status`.
 *
 * The backend allows at most one such operation per file at a time (it takes
 * a file-level lock), so a single current record is sufficient — this is
 * deliberately not a queue or a history.
 */
export type TFileOperationStatus = {
    fileId: string;
    operation: string | null;
    state: "running" | "succeeded" | "failed" | null;
    error: string | null;
};

/**
 * File operation slice state.
 *
 * `null` means "not loaded for the current file yet" (e.g. before the first
 * `operation-status` fetch on mount) — distinct from a loaded status whose
 * `state` is `null` (no operation has ever run on the file).
 */
export type TSliceFileOperationState = {
    fileOperationStatus: TFileOperationStatus | null;
};

/**
 * File operation slice actions.
 */
export type TSliceFileOperationActions = {
    /** Replace the current operation snapshot — called on load/seed and on every poll tick. */
    setFileOperationStatus: (status: TFileOperationStatus) => void;
    /** Drop the current snapshot, e.g. when navigating away from the file. */
    clearFileOperationStatus: () => void;
};

/**
 * Complete file operation slice type.
 */
export type TSliceFileOperation = TSliceFileOperationState & TSliceFileOperationActions;

const initialState: TSliceFileOperationState = {
    fileOperationStatus: null,
};

/**
 * Creates the file operation slice for the store.
 *
 * This is what lets the review page's action buttons survive a page reload
 * or navigation while a background operation is still running: the buttons
 * gate on this server-derived value (seeded from `operation-status` on load,
 * not just held in per-button local component state), so a reload mid-run
 * still shows the operation as running and keeps them disabled instead of
 * silently re-enabling and letting a second click 409 against the file lock.
 */
export const createSliceFileOperation = (): TStateCreator<TSliceFileOperation> => {
    return (set) => ({
        ...initialState,

        setFileOperationStatus: (status: TFileOperationStatus) => {
            set({ fileOperationStatus: status });
        },

        clearFileOperationStatus: () => {
            set({ fileOperationStatus: null });
        },
    });
};
